import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text as ChakraText,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spacer,
} from "@chakra-ui/react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import {
  RiChat1Line,
  RiDraggable,
  RiEyeLine,
  RiListCheck,
  RiSortDesc,
} from "react-icons/ri";
import { useAppState } from "../../../../components/App/AppProvider";
import {
  DeleteIcon,
  ExportIcon,
  MoreMenuIcon,
} from "../../../../constants/commonIcons";
import { hasAccessTo } from "../../../../shared/_core/limit.utils";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import useToast from "../../../../utilities/useToast";
import { exportManualSection } from "../utils/exportManual";
import ManualCommentsSection from "./ManualCommentsSection";

type SectionItem = {
  label: string;
  link: string;
  id: string;
  jsonStr: string;
  title: string;
  manualTitle: string;
  lastModified: Date | null;
  modifiedBy: {
    userID: string;
    firstName: string;
    lastName: string;
  };
};

/**
 * Gets the items in a format suitable for reordering.
 * @param items
 * @returns
 */
const getMappedItems = (items: SectionItem[]): ReorderableItem[] => {
  return items.map((item, index) => ({
    ...item,
    sectionID: item.id,
    originalIndex: index,
    newIndex: index,
  }));
};

type Props = {
  items: SectionItem[];
  manualDetails: {
    manualID: string;
    responsibleUser: string;
  };
  onClick: (index: number) => void;
  activeIndex: number;
  onClickDelete: (id: string, name: string) => void;
  onClickUsage: (id: string, name: string) => void;
};

type ReorderableItem = {
  label: string;
  link: string;
  sectionID: string;
  originalIndex: number;
  newIndex: number;
};

function findChangedPositions(
  originalItems: ReorderableItem[],
  newItems: ReorderableItem[],
) {
  const changes: ReorderableItem[] = [];

  const originalIndexMap = new Map<string, number>();
  originalItems.forEach((item, index) => {
    originalIndexMap.set(item.label, index);
  });

  newItems.forEach((item, newIndex) => {
    const originalIndex = originalIndexMap.get(item.label);

    if (originalIndex !== undefined && originalIndex !== newIndex) {
      changes.push({
        ...item,
        originalIndex,
        newIndex,
      });
    }
  });

  return changes;
}

export default function TableOfContents({
  items,
  onClick,
  activeIndex,
  manualDetails,
  onClickDelete,
  onClickUsage,
}: Props) {
  const sectionItems = getMappedItems(items);

  const [isEditing, setIsEditing] = useState(false);
  const [initalLoad, setInitalLoad] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [reordarableItems, setReordarableItems] = useState<ReorderableItem[]>(
    [],
  );
  const [originalItems, setOriginalItems] = useState<ReorderableItem[]>([]);

  const updateSectionOrder = useApiMutation("manual/updateManualSectionOrder");
  const queryClient = useQueryClient();
  const { permissions } = useAppState().app;
  const { app } = useAppState();
  const { displayToast } = useToast();
  const isOwn = manualDetails.responsibleUser === permissions.INFO.userID;

  const canEdit =
    permissions.MANUAL.UPDATE.ALL || (permissions.MANUAL.UPDATE.SELF && isOwn);
  const canExportManualSection = hasAccessTo("export", app.license);

  const exportToPDF = useApiMutation("manual/exportToPDF");
  const exportToDOCX = useApiMutation("manual/exportToDOCX");

  const isMutating = exportToPDF.isPending || exportToDOCX.isPending;

  // Comments query for count display only
  const totalCommentsQuery = useApiQuery("comments/findMany", {
    data: {
      manualID: [manualDetails.manualID],
    },
  });

  const getCommentCount = () => {
    return totalCommentsQuery.data?.comments?.length || 0;
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleExportSectionPDF = async (item: SectionItem) => {
    const htmlStr = exportManualSection(item, {
      logoPath: app.accountInfo.logoPath,
      newLogoPath: app.accountInfo.newLogoPath,
    });
    const result = await exportToPDF.mutateAsync({
      htmlContent: htmlStr,
      fileName: item.title || "Manual Section",
    });
    const link = document.createElement("a");
    link.href = result.url || "";
    link.download = `${item.title || "ManualSection"}.pdf`;
    link.click();
  };

  const handleExportSectionDocx = async (item: SectionItem) => {
    const htmlStr = exportManualSection(item, {
      logoPath: app.accountInfo.logoPath,
      newLogoPath: app.accountInfo.newLogoPath,
    });

    const result = await exportToDOCX.mutateAsync({
      htmlContent: htmlStr,
      fileName: item.title || "Manual Section",
    });
    const link = document.createElement("a");
    link.href = result.url || "";
    link.download = `${item.title || "ManualSection"}.docx`;
    link.click();
  };

  const resetReordableItems = () => {
    // Original items is used to get the difference between the original and reordered items
    setReordarableItems([...sectionItems]);
    setOriginalItems([...sectionItems]);
  };

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;
    const reorderedItems = Array.from(reordarableItems || []);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    if (removed) {
      reorderedItems.splice(result.destination.index, 0, removed);
    }
    setReordarableItems(
      reorderedItems.map((item, index) => ({ ...item, newIndex: index })),
    );
  };

  const handleEditClick = () => {
    resetReordableItems();
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    resetReordableItems();
    setIsEditing(false);
  };

  const handleSaveClick = async () => {
    try {
      const reordered = findChangedPositions(originalItems, reordarableItems);
      await updateSectionOrder.mutateAsync({
        sections: reordered,
        manualID: manualDetails.manualID,
      });
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ["manual"] });
      displayToast({
        title: "Accepted",
        description: "Sections reordered successfully",
        status: "success",
      });
    } catch (error) {
      resetReordableItems();
      setIsEditing(false);
      displayToast({
        title: "Error",
        description: "Failed to reorder sections. Please try again.",
        status: "error",
      });
      console.error("Error updating section order:", error);
    }
  };
  const linkHeight = 36;
  const indicatorSize = 10;
  const indicatorOffset = (linkHeight - indicatorSize) / 2;

  useEffect(() => {
    if (activeIndex < 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const dotTop = activeIndex * linkHeight + indicatorOffset;
    const { scrollTop, clientHeight } = container;
    const visibleTop = scrollTop + 4; // small threshold added for visibility
    const visibleBottom = scrollTop + clientHeight;

    if (dotTop < visibleTop) {
      container.scrollTop = dotTop;
    } else if (dotTop + indicatorSize > visibleBottom) {
      container.scrollTop = dotTop + indicatorSize - clientHeight;
    }
  }, [activeIndex, linkHeight, indicatorOffset]);

  const itemsToRender = isEditing ? reordarableItems : sectionItems;

  const renderedItems = itemsToRender.map((item, index) => {
    const menuHidden = !isEditing && hoveredIndex !== index;

    return (
      <Draggable
        key={item.label}
        draggableId={item.label}
        isDragDisabled={!isEditing}
        index={index}>
        {(provided) => (
          <HStack
            _hover={{ bg: "gray.50", textDecor: "none" }}
            w="full"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}>
            <Box
              w="full"
              as="a"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();
                onClick(index);
              }}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              display="flex"
              textDecor="none"
              fontSize="sm"
              height={9}
              lineHeight={9}
              borderTopRightRadius="md"
              borderBottomRightRadius="md"
              borderLeftWidth="2px"
              borderLeftStyle="solid"
              cursor={isEditing ? "move" : "pointer"}
              fontWeight={activeIndex === index ? "500" : undefined}
              color={activeIndex === index ? "brand.800" : "gray.800"}
              px={4}
              noOfLines={1}>
              {isEditing ? <Icon as={RiDraggable} size="md" /> : null}
              {item.label}
            </Box>
            <Menu size="sm">
              <MenuButton
                visibility={isEditing ? "hidden" : "visible"}
                variant="link"
                isLoading={isMutating}
                hidden={menuHidden}
                as={IconButton}
                icon={<MoreMenuIcon />}
              />
              <MenuList>
                <MenuItem
                  onClick={() => onClickUsage(item.sectionID, item.label)}
                  icon={<RiEyeLine />}>
                  Usage
                </MenuItem>
                <MenuDivider />
                <Tooltip
                  label={
                    !canExportManualSection
                      ? "Upgrade your license to export this section"
                      : undefined
                  }>
                  <MenuItem
                    onClick={() => handleExportSectionPDF(item)}
                    isDisabled={!canExportManualSection}
                    icon={<ExportIcon />}>
                    Export as PDF
                  </MenuItem>
                </Tooltip>
                <Tooltip
                  label={
                    !canExportManualSection
                      ? "Upgrade your license to export this section"
                      : undefined
                  }>
                  <MenuItem
                    onClick={() => handleExportSectionDocx(item)}
                    isDisabled={!canExportManualSection}
                    icon={<ExportIcon />}>
                    Export as DOCX
                  </MenuItem>
                </Tooltip>
                <MenuItem
                  icon={<DeleteIcon />}
                  color="red.800"
                  onClick={() => onClickDelete(item.sectionID, item.label)}
                  isDisabled={!canEdit}>
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        )}
      </Draggable>
    );
  });

  return (
    <>
      <Box
        _focusWithin={{
          ".hover-button": {
            visibility: "visible",
            opacity: 1,
          },
        }}
        _hover={{
          ".hover-button": {
            visibility: "visible",
            opacity: 1,
          },
        }}
        pos="relative"
        height="calc(100vh - 120px)"
        overflowY="auto"
        pl="1">
        <HStack align="center" marginBottom="4" w="100%">
          <Tabs variant="line" w="100%" marginBottom="4">
            <TabList>
              <Tab>
                <HStack spacing={2} align="center">
                  <RiListCheck style={{ width: 16, height: 16 }} />
                  <ChakraText>Sections</ChakraText>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2} align="center">
                  <RiChat1Line style={{ width: 16, height: 16 }} />
                  <ChakraText>Comments ({getCommentCount()})</ChakraText>
                </HStack>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <ChakraText fontSize="sm" color="gray.600">
                  <HStack
                    w="100%"
                    spacing={2}
                    pr="4"
                    align="center"
                    justify="end">
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          colorScheme="gray"
                          onClick={handleCancelClick}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveClick}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <Box
                        visibility="hidden"
                        opacity={0}
                        className="hover-button">
                        <Tooltip
                          label={
                            canEdit
                              ? "Reorder Sections"
                              : "You do not have permission to reorder this manual"
                          }>
                          <IconButton
                            aria-label="edit"
                            variant="ghost"
                            size="sm"
                            isDisabled={!canEdit}
                            onClick={handleEditClick}
                            icon={<RiSortDesc />}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </HStack>
                  {items.length >= 1 ? (
                    <Box
                      ref={scrollContainerRef}
                      height="calc(100vh - 450px)"
                      overflowY="auto"
                      pos="relative"
                      pl="2">
                      <Box
                        transition="transform 150ms ease"
                        borderWidth="2px"
                        background="white"
                        borderColor="brand.800"
                        borderStyle="solid"
                        height={`${indicatorSize}px`}
                        width={`${indicatorSize}px`}
                        borderRadius={`${indicatorSize}px`}
                        pos="absolute"
                        left="-1px"
                        display={activeIndex < 0 ? "none" : undefined}
                        transform={`translate(50%, ${
                          activeIndex * linkHeight + indicatorOffset
                        }px)`}
                      />
                      <DragDropContext onDragEnd={handleOnDragEnd}>
                        <Droppable droppableId="droppable">
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}>
                              {renderedItems}
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Box>
                  ) : (
                    <ChakraText color="gray.600">Manual is empty...</ChakraText>
                  )}{" "}
                </ChakraText>
              </TabPanel>
              <TabPanel p={0}>
                <ManualCommentsSection
                  manualDetails={manualDetails}
                  items={items}
                  onClick={onClick}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </HStack>
      </Box>
      <Spacer />
    </>
  );
}
